/* eslint-disable import/order */
import {
  SkuDebugResponse,
  SupplierCatalogService,
} from 'app/providers/api/services/supplierCatalog.service';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface Props {
  jobId: string;
  initialSku?: string;
}

export const MatchDebugTab = ({ jobId, initialSku }: Props) => {
  const { t } = useTranslation();
  const [supplierSku, setSupplierSku] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [debugResult, setDebugResult] = useState<SkuDebugResponse | null>(null);

  const executeDebugForSku = useCallback(
    async (sku: string) => {
      if (!sku.trim()) {
        toast.error('Please enter a supplier SKU');
        return;
      }

      setIsLoading(true);
      setDebugResult(null);

      try {
        const result = await SupplierCatalogService.debugSkuMatching({
          jobId,
          supplierSku: sku.trim(),
        });
        setDebugResult(result);
      } catch (error: any) {
        console.error('Debug SKU matching error:', error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to debug SKU matching',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [jobId],
  );

  const handleDebug = async () => {
    await executeDebugForSku(supplierSku);
  };

  const formatJson = (obj: any) => {
    return JSON.stringify(obj, null, 2);
  };

  // Handle initial SKU and auto-execute debug
  useEffect(() => {
    if (initialSku && initialSku !== supplierSku) {
      setSupplierSku(initialSku);
      // Auto-execute debug after setting the SKU
      setTimeout(() => {
        executeDebugForSku(initialSku);
      }, 100);
    }
  }, [initialSku, supplierSku, executeDebugForSku]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {t('SKU Matching Debug')}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mb: 2 }}>
            <TextField
              label={t('Supplier SKU')}
              value={supplierSku}
              onChange={(e) => setSupplierSku(e.target.value)}
              placeholder={t('Enter supplier SKU to debug')}
              fullWidth
              variant="outlined"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleDebug();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleDebug}
              disabled={isLoading || !supplierSku.trim()}
              sx={{ minWidth: 120, height: 56 }}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('Debug')
              )}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary">
            {t(
              'Enter a supplier SKU to see the exact and semantic matching results in raw JSON format.',
            )}
          </Typography>
        </CardContent>
      </Card>

      {debugResult && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('Debug Results')}
            </Typography>

            <Box
              component="pre"
              sx={{
                backgroundColor: '#f5f5f5',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
                overflow: 'auto',
                maxHeight: '70vh',
                fontSize: '0.875rem',
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {formatJson(debugResult)}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
